import Modal from "common/Modal/Modal";
import Flex from "common/Boxes/Flex";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import styled, { useTheme } from "styled-components";
import { copyImgToClipboard } from "utils/misc";
import React, { useState } from "react";
import { Download } from "common/Icons/Icons";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Button from "common/Buttons/Button";
import Link from "common/Typography/Link";
import useMedia from "utils/hooks/useMedia";
import { IS_WEB_VIEW } from "common/constants.ts";
import useDownloadFile from "Global/useDownloadFile.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants.ts";

interface ShareImageModalProps {
  visible: boolean;
  closeModal: () => void;
  imageUrl?: string;
  imageTitle?: string;
}

const ShareImageModal = ({
  visible,
  closeModal,
  imageUrl,
  imageTitle,
}: ShareImageModalProps) => {
  const theme = useTheme();
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const isMobile = useMedia([`(max-width: 550px)`], [true], false);
  const {
    isLoading: isLoadingShare,
    setIsLoading: setIsLoadingShare,
    downloadFile,
  } = useDownloadFile();

  const onClose = () => {
    setIsLoadingShare(false);
    setIsLoadingImage(false);
    closeModal();
  };

  const buttons = (
    <ButtonsWrapper $isMobile={isMobile}>
      <CancelButton $isMobile={isMobile} onClick={closeModal}>
        Cancel
      </CancelButton>
      {!IS_TELEGRAM_WEB_APP && !IS_WEB_VIEW && (
        <ImageActionButton
          type="primary"
          $isMobile={isMobile}
          onClick={() => copyImgToClipboard(imageUrl)}
        >
          <CopyIcon />
          Copy
        </ImageActionButton>
      )}
      <ImageActionButton
        type="primary"
        $isMobile={isMobile}
        loading={isLoadingShare}
        onClick={() => downloadFile(imageUrl || "", `${imageTitle}.png`)}
      >
        {IS_WEB_VIEW ? (
          "Share"
        ) : (
          <>
            <DownloadIcon />
            Download
          </>
        )}
      </ImageActionButton>
    </ButtonsWrapper>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={false}
      footer={false}
      closable={false}
      width={520}
      centered={true}
      styles={{
        body: { padding: 0 },
      }}
    >
      <LoadingSpinnerOverlay
        isLoading={isLoadingImage}
        wrap={true}
        hideContentWhileLoading={true}
        wrapStyle={{ minHeight: isMobile ? 190 : 290 }}
        backgroundColor={theme.colors.cardBgColor}
      >
        <ImagePreview
          src={imageUrl}
          $isMobile={isMobile}
          onLoad={() => setIsLoadingImage(false)}
          key={imageUrl}
        />
      </LoadingSpinnerOverlay>
      {buttons}
    </Modal>
  );
};

export default React.memo(ShareImageModal);

const ImagePreview = styled.img<{ $isMobile: boolean }>`
  border-radius: 8px 8px 0 0;
  width: 100%;
  min-height: ${(p) => (p.$isMobile ? 170 : 290)}px;
`;
const ButtonsWrapper = styled(Flex)<{ $isMobile: boolean }>`
  grid-gap: 8px;
  justify-content: flex-end;
  padding: ${(p) => (p.$isMobile ? 12 : 16)}px;
`;
const ImageActionButton = styled(Button)<{ $isMobile: boolean }>`
  fill: ${(p) => p.theme.colors.white};
  ${(p) =>
    p.$isMobile &&
    `
  padding-right: 10px;
  padding-left: 10px;
  `}
`;
const CancelButton = styled(Link)<{ $isMobile: boolean }>`
  margin-right: ${(p) => (p.$isMobile ? 4 : 12)}px;
`;
const DownloadIcon = styled(Download)`
  top: 3px;
  margin-right: 8px;
  position: relative;
  fill: ${(p) => p.theme.colors.buttonText};
`;
const CopyIcon = styled(CopyOutlined)`
  position: relative;
  top: 1px;
`;
